import { useProjectId, usePackageId, useUpdateFindingMutation } from '@consigli/hooks';
import { Finding, FindingStatus } from '@consigli/types';
import { useState } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton, ObjectDetailView } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { EditFindingModal } from './edit-finding-modal';

export const FindingActionButton = ({ finding }: { finding: Finding }) => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [updateFinding] = useUpdateFindingMutation();

  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const onViewDetails = () => {
    setIsDetailViewOpen(true);
  };

  const onEdit = () => {
    setIsEditModalOpen(true);
  };

  const onDelete = async () => {
    try {
      await updateFinding({
        projectId,
        findingId: finding.id,
        packageId,
        data: {
          status: FindingStatus.DELETED,
        },
      }).unwrap();

      successToast({ message: 'Finding deleted successfully!' });
    } catch (error) {
      console.error('Error deleting finding:', error);
      errorToast({ message: 'Failed to delete finding. Please try again.' });
    }
  };

  const onEditConfirm = async (updatedData: Partial<Finding>) => {
    try {
      await updateFinding({
        projectId,
        findingId: finding.id,
        packageId,
        data: updatedData,
      }).unwrap();

      successToast({ message: 'Finding updated successfully!' });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating finding:', error);
      errorToast({ message: 'Failed to update finding. Please try again.' });
    }
  };

  const onEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const actions = [
    { label: 'View Details', onClick: () => onViewDetails() },
    { label: 'Edit', onClick: () => onEdit() },
    { label: 'Delete', onClick: () => onDelete(), destructive: true },
  ];

  return (
    <>
      <ActionButton icon={<BsThreeDots />} actions={actions} />

      <ObjectDetailView
        object={finding}
        objectType="Finding"
        summary={`Finding details including status, content, and metadata.`}
        isOpen={isDetailViewOpen}
        onOpenChange={(open: boolean) => {
          setIsDetailViewOpen(open);
        }}
      />

      <EditFindingModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        finding={finding}
        onConfirm={onEditConfirm}
        onCancel={onEditCancel}
        isSubmitting={false}
      />
    </>
  );
};
